import React, { useEffect, useState } from "react";
import { Flash, Button, IconButton, Box, Text, RelativeTime } from "@primer/react";
import { CheckCircleIcon, AlertIcon, DownloadIcon, XIcon, SyncIcon, BlockedIcon } from "@primer/octicons-react";
import { triggerDownload } from "./utils";

export interface ToastNotificationProps {
  message: string;
  variant?: "success" | "danger" | "warning";
  downloadUrl?: string;
  expiresAt?: string;
  subtitle?: string;
  onClose: () => void;
  autoCloseMs?: number;
  icon?: "check" | "alert" | "sync" | "blocked";
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  message,
  variant = "success",
  downloadUrl,
  expiresAt,
  subtitle,
  onClose,
  autoCloseMs = 6000,
  icon,
}) => {
  const isError = variant === "danger";
  const isWarning = variant === "warning";
  
  // Determine icon based on prop or fallback to variant-based defaults
  let StatusIcon;
  if (icon === "sync") {
    StatusIcon = SyncIcon;
  } else if (icon === "blocked") {
    StatusIcon = BlockedIcon;
  } else if (icon === "alert" || isError) {
    StatusIcon = AlertIcon;
  } else {
    StatusIcon = CheckCircleIcon;
  }
  
  const iconColor = isError ? "danger.fg" : isWarning ? "attention.fg" : "success.fg";
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (autoCloseMs > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }, autoCloseMs);

      return () => clearTimeout(timer);
    }
  }, [autoCloseMs, onClose]);

  const handleDownload = () => {
    if (downloadUrl) {
      triggerDownload(downloadUrl);
    }
  };

  if (!isVisible) return null;

  return (
    <Flash variant={variant}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
        <Box sx={{ flexShrink: 0, color: iconColor, display: "flex" }}>
          <StatusIcon size={16} className={icon === "sync" ? "anim-rotate" : undefined} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text sx={{ fontWeight: "semibold", display: "block" }}>{message}</Text>
          {subtitle && (
            <Text sx={{ fontSize: 0, color: "fg.muted", display: "block", mt: 1 }}>
              {subtitle}
            </Text>
          )}
          {expiresAt && (
            <Text sx={{ fontSize: 0, color: "fg.muted", display: "block", mt: 1 }}>
              Download expires <RelativeTime date={new Date(expiresAt)} />
            </Text>
          )}
          {downloadUrl && (
            <Box sx={{ mt: 2 }}>
              <Button size="small" onClick={handleDownload} leadingVisual={DownloadIcon}>
                Download now
              </Button>
            </Box>
          )}
        </Box>
        <IconButton
          icon={XIcon}
          variant="invisible"
          size="small"
          aria-label="Dismiss"
          onClick={onClose}
          sx={{ flexShrink: 0 }}
        />
      </Box>
    </Flash>
  );
};

export default ToastNotification;
